/**
 * ExecutiveDashboardPage — workflow-first operational cockpit.
 *
 * Polish-pass rewrite (UI/UX only):
 *   - No backend / API / DB / RBAC / business-logic changes
 *   - All pre-existing data-testid values preserved (executive-dashboard,
 *     branch-filter, kpi-*, ai-recommendations-widget, ai-risk-summary-widget)
 *   - New testids are additive only (today-ops-strip, today-ops-tile-*,
 *     urgent-actions-strip, dashboard-greeting, etc.)
 *
 * Hierarchy (top → bottom):
 *   1. Header (greeting + branch filter + refresh)
 *   2. Urgent Actions strip (renders only if critical/high alerts > 0)
 *   3. Today's Operations pulse band (only tiles with real data)
 *   4. Approvals & Issues row (role-aware)
 *   5. KPI bands (role-aware: People & Ops / Compliance & Clinical / Financial)
 *   6. AI Insights row (admin-only)
 *   7. Footer (data sources + period chip)
 *
 * Sparklines / DeltaChips were planned but DROPPED because the existing
 * /kpi/executive endpoint does not return history or previous-period data.
 * Per the "only existing backend data" rule we gracefully omit them rather
 * than fabricate metrics.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { DASHBOARD_TARGETS as T } from '@/lib/dashboardTargets';   // NAV-1
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Users, UserCircle, DollarSign, AlertTriangle, Calendar,
  RefreshCw, Building2, ChevronRight, Clock, Pill, FileText,
  Banknote, ClipboardList, Sparkles, Lightbulb, Bot, ArrowRight,
} from 'lucide-react';
import { AchievementDashboardTile } from '@/components/participants/outcomes/OutcomeSummaryCard';

/* ─────────────────────────────────────────────────────────────────────────────
 * Role-aware visibility map (computed from user.role_name).
 *
 * Note: /dashboard is already gated by `requireAdmin`, so Support Worker /
 * Staff never land here — they're redirected to /staff/dashboard. This map
 * differentiates between admin-tier roles only.
 * ───────────────────────────────────────────────────────────────────────────*/
function useRoleView(roleName) {
  return useMemo(() => {
    const role = roleName || '';
    const isSuperOrAdmin = ['Super Admin', 'Admin'].includes(role);
    const isFinance = role === 'Finance';
    const isCoordinator = ['Coordinator', 'Roster Manager'].includes(role);
    const isHR = role === 'HR';

    // Default: show everything (Super Admin / Admin).
    let view = {
      showPeopleOps: true,
      showClinical: true,
      showFinancial: true,
      showAI: true,
      greetingSuffix: 'organisation-wide view',
    };
    if (isFinance) {
      view = { ...view, showClinical: false, greetingSuffix: 'finance view' };
    } else if (isCoordinator) {
      view = { ...view, showFinancial: false, showAI: false, greetingSuffix: 'operations view' };
    } else if (isHR) {
      view = { ...view, showFinancial: false, greetingSuffix: 'people & compliance view' };
    } else if (!isSuperOrAdmin) {
      // Any other admin-tier role — keep cautious: hide AI insights only.
      view = { ...view, showAI: false };
    }
    return view;
  }, [roleName]);
}

/* ─── Small presentational primitives (local — not shared) ─────────────────*/

function KpiCard({ title, value, subtitle, icon: Icon, tone = 'neutral', onClick, testid }) {
  const tones = {
    neutral: 'border-slate-200 hover:border-slate-300',
    positive: 'border-emerald-200/70 bg-emerald-50/30 hover:border-emerald-300',
    caution: 'border-amber-200/70 bg-amber-50/30 hover:border-amber-300',
    critical: 'border-red-200/70 bg-red-50/30 hover:border-red-300',
  };
  const iconBg = {
    neutral: 'bg-slate-100 text-slate-600',
    positive: 'bg-emerald-100 text-emerald-700',
    caution: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group w-full text-left rounded-lg border bg-white p-4 transition-colors ${tones[tone]} focus:outline-none focus:ring-2 focus:ring-slate-300`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</div>
          <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
          {subtitle && <div className="mt-1 text-xs text-slate-500 truncate">{subtitle}</div>}
        </div>
        {Icon && (
          <div className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center ${iconBg[tone]}`}>
            <Icon className="h-4 w-4" aria-hidden />
          </div>
        )}
      </div>
    </button>
  );
}

function PulseTile({ label, value, hint, tone = 'neutral', onClick, testid }) {
  const tones = {
    neutral: 'border-slate-200',
    caution: 'border-amber-300 bg-amber-50/40',
    critical: 'border-red-300 bg-red-50/40',
    positive: 'border-emerald-300 bg-emerald-50/40',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group w-full rounded-md border bg-white px-3 py-2.5 text-left transition-colors ${tones[tone]} hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
        {tone === 'critical' && <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" aria-hidden />}
        {tone === 'caution' && <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden />}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums text-slate-900">{value}</span>
        {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
      </div>
    </button>
  );
}

function SectionHeading({ children, testid }) {
  return (
    <h2
      data-testid={testid}
      className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2.5"
    >
      {children}
    </h2>
  );
}

function LoadingShell() {
  return (
    <div className="space-y-6" data-testid="executive-dashboard-loading">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-3 w-72 mt-2" />
        </div>
        <Skeleton className="h-9 w-44" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-md" />
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────────*/

export default function ExecutiveDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');
  const [aiInsights, setAiInsights] = useState(null);

  const view = useRoleView(user?.role_name);

  const fetchData = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const params = selectedBranch ? { branchId: selectedBranch } : {};
      const [kpiRes, alertRes, branchRes, recsRes, riskRes] = await Promise.all([
        api.get('/kpi/executive', { params }),
        api.get('/alerts/count', { params }),
        api.get('/org/branches'),
        api.get('/recommendations', { params: { ...params, status: 'ACTIVE', pageSize: 5 } }).catch(() => ({ data: { items: [] } })),
        api.get('/risk-scores/summary', { params }).catch(() => ({ data: { data: {} } })),
      ]);
      setKpis(kpiRes.data.data);
      setAlerts(alertRes.data.data);
      setBranches(branchRes.data.data || []);
      setAiInsights({
        recommendations: recsRes.data.items || [],
        riskSummary: riskRes.data.data || {},
      });
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch]); // eslint-disable-line

  const handleBranchChange = (value) => {
    setSelectedBranch(value === 'all' ? '' : value);
    if (value && value !== 'all') {
      setSearchParams({ branchId: value });
    } else {
      setSearchParams({});
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  if (loading && !kpis) return <LoadingShell />;

  const greetingName = (user?.full_name || '').split(' ')[0] || 'there';
  const hourNow = new Date().getHours();
  const timeOfDay = hourNow < 12 ? 'Good morning' : hourNow < 18 ? 'Good afternoon' : 'Good evening';

  // Phase 4.5: derive flag from the localStorage cache that AuthContext keeps in sync.
  const outcomesReportingFlagEnabled = (() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      return !!flags.goal_outcomes_reporting_enabled;
    } catch { return false; }
  })();

  const criticalCount = alerts?.CRITICAL || 0;
  const highCount = alerts?.HIGH || 0;
  const hasUrgent = criticalCount + highCount > 0;

  /* Today's Operations tiles — only render tiles backed by real data. */
  const todayTiles = [];
  if (kpis?.openShifts !== undefined) {
    todayTiles.push({
      key: 'open-shifts',
      label: 'Open shifts',
      value: kpis.openShifts || 0,
      hint: kpis.openShifts > 0 ? 'unfilled' : 'all covered',
      tone: kpis.openShifts > 5 ? 'critical' : kpis.openShifts > 0 ? 'caution' : 'positive',
      onClick: () => navigate(T.openShifts()),
    });
  }
  if (kpis?.pendingTimesheets !== undefined) {
    todayTiles.push({
      key: 'pending-timesheets',
      label: 'Pending approval',
      value: kpis.pendingTimesheets || 0,
      hint: 'timesheets',
      tone: kpis.pendingTimesheets > 20 ? 'caution' : 'neutral',
      onClick: () => navigate(T.pendingTimesheets()),
    });
  }
  if (kpis?.incidentsThisWeek !== undefined) {
    todayTiles.push({
      key: 'incidents-week',
      label: 'Incidents (week)',
      value: kpis.incidentsThisWeek || 0,
      hint: kpis?.details?.incidents?.open ? `${kpis.details.incidents.open} open` : null,
      tone: kpis.incidentsThisWeek > 2 ? 'critical' : kpis.incidentsThisWeek > 0 ? 'caution' : 'positive',
      onClick: () => navigate(T.incidentsWeek()),
    });
  }
  if (alerts && alerts.total !== undefined) {
    todayTiles.push({
      key: 'critical-alerts',
      label: 'Critical alerts',
      value: criticalCount,
      hint: highCount ? `+${highCount} high` : 'last 24h',
      tone: criticalCount > 0 ? 'critical' : highCount > 0 ? 'caution' : 'positive',
      onClick: () => navigate(T.criticalAlerts()),
    });
  }

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* ─── Header ───────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="dashboard-greeting">
            {timeOfDay}, {greetingName}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {view.greetingSuffix}
            {kpis?.period?.dateFrom && (
              <>
                {' · '}
                <span className="tabular-nums">{kpis.period.dateFrom} → {kpis.period.dateTo}</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedBranch || 'all'} onValueChange={handleBranchChange}>
            <SelectTrigger className="w-[200px] h-9" data-testid="branch-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => fetchData({ silent: true })}
            disabled={refreshing}
            data-testid="dashboard-refresh"
            aria-label="Refresh dashboard"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* ─── Urgent Actions strip (only when alerts > 0) ──────────── */}
      {hasUrgent && (
        <div
          data-testid="urgent-actions-strip"
          className="rounded-lg border border-red-200 bg-gradient-to-r from-red-50 to-amber-50/40 px-4 py-3 flex items-start gap-3 sm:items-center sm:flex-row flex-col sm:justify-between"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Needs your attention</div>
              <div className="text-xs text-slate-600 mt-0.5">
                {criticalCount > 0 && <span className="font-medium text-red-700">{criticalCount} critical</span>}
                {criticalCount > 0 && highCount > 0 && <span className="text-slate-400"> · </span>}
                {highCount > 0 && <span className="font-medium text-amber-700">{highCount} high-priority</span>}
                <span> alert{criticalCount + highCount === 1 ? '' : 's'} in the last 24h</span>
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" asChild data-testid="urgent-actions-view-all">
            <Link to="/alerts" className="gap-1.5">
              Review now <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      )}

      {/* Phase 4.5: Goal Achievement (90-day) — flag-gated. */}
      {outcomesReportingFlagEnabled && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="dashboard-outcomes-row">
          <AchievementDashboardTile />
        </div>
      )}

      {/* ─── Today's Operations pulse band ────────────────────────── */}
      {todayTiles.length > 0 && (
        <section>
          <SectionHeading testid="today-ops-heading">Today's operations</SectionHeading>
          <div
            data-testid="today-ops-strip"
            className="grid grid-cols-2 gap-2.5 sm:grid-cols-4"
          >
            {todayTiles.map((t) => (
              <PulseTile
                key={t.key}
                label={t.label}
                value={t.value}
                hint={t.hint}
                tone={t.tone}
                onClick={t.onClick}
                testid={`today-ops-tile-${t.key}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* ─── KPI bands (role-aware) ──────────────────────────────── */}
      {view.showPeopleOps && (
        <section>
          <SectionHeading testid="people-ops-heading">People & operations</SectionHeading>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              testid="kpi-active-participants"
              title="Active participants"
              value={kpis?.activeParticipants ?? 0}
              subtitle={`${kpis?.details?.participants?.total ?? 0} total`}
              icon={UserCircle}
              onClick={() => navigate(T.activeParticipants())}
            />
            <KpiCard
              testid="kpi-active-staff"
              title="Active staff"
              value={kpis?.activeStaff ?? 0}
              subtitle={`${kpis?.details?.staff?.total ?? 0} records`}
              icon={Users}
              onClick={() => navigate(T.activeStaff())}
            />
            <KpiCard
              testid="kpi-roster-fill-rate"
              title="Roster fill rate"
              value={`${kpis?.rosterFillRate ?? 0}%`}
              subtitle={`${kpis?.details?.shifts?.filled ?? 0} of ${kpis?.details?.shifts?.total ?? 0} shifts`}
              icon={Calendar}
              tone={kpis?.rosterFillRate >= 80 ? 'positive' : kpis?.rosterFillRate >= 50 ? 'caution' : 'critical'}
              onClick={() => navigate(T.rosterFillRate())}
            />
            <KpiCard
              testid="kpi-open-shifts"
              title="Open shifts"
              value={kpis?.openShifts ?? 0}
              subtitle="Unfilled from today"
              icon={Clock}
              tone={kpis?.openShifts > 10 ? 'critical' : kpis?.openShifts > 5 ? 'caution' : 'neutral'}
              onClick={() => navigate(T.openShifts())}
            />
          </div>
        </section>
      )}

      {view.showClinical && (
        <section>
          <SectionHeading testid="compliance-clinical-heading">Compliance & clinical</SectionHeading>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              testid="kpi-pending-timesheets"
              title="Pending timesheets"
              value={kpis?.pendingTimesheets ?? 0}
              subtitle="Awaiting approval"
              icon={ClipboardList}
              tone={kpis?.pendingTimesheets > 20 ? 'caution' : 'neutral'}
              onClick={() => navigate(T.pendingTimesheets())}
            />
            <KpiCard
              testid="kpi-incidents-this-week"
              title="Incidents this week"
              value={kpis?.incidentsThisWeek ?? 0}
              subtitle={`${kpis?.details?.incidents?.open ?? 0} currently open`}
              icon={AlertTriangle}
              tone={kpis?.incidentsThisWeek > 5 ? 'critical' : kpis?.incidentsThisWeek > 2 ? 'caution' : 'neutral'}
              onClick={() => navigate(T.incidentsWeek())}
            />
            <KpiCard
              testid="kpi-medication-misses"
              title="Medication misses"
              value={kpis?.medicationMisses ?? 0}
              subtitle="This week"
              icon={Pill}
              tone={kpis?.medicationMisses > 0 ? 'critical' : 'positive'}
              onClick={() => navigate(T.medicationMisses())}
            />
            <KpiCard
              testid="kpi-claims-pending"
              title="Claims pending"
              value={kpis?.claimsPending ?? 0}
              subtitle={kpis?.details?.claims?.pendingAmount ? formatCurrency(kpis.details.claims.pendingAmount) : 'Ready for submission'}
              icon={FileText}
              tone={kpis?.claimsPending > 10 ? 'caution' : 'neutral'}
              onClick={() => navigate(T.claimsPending())}
            />
          </div>
        </section>
      )}

      {view.showFinancial && (
        <section>
          <SectionHeading testid="financial-heading">Financial overview</SectionHeading>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard
              testid="kpi-payroll-cost-(period)"
              title="Payroll cost"
              value={formatCurrency(kpis?.payrollCostThisPeriod || 0)}
              subtitle={`${kpis?.details?.payroll?.hours ?? 0} hours`}
              icon={Banknote}
              onClick={() => navigate(T.payrollCost())}
            />
            <KpiCard
              testid="kpi-revenue-estimate"
              title="Revenue estimate"
              value={formatCurrency(kpis?.revenueEstimate || 0)}
              subtitle={`${kpis?.details?.revenue?.claimsCount ?? 0} claims paid`}
              icon={DollarSign}
              tone="positive"
              onClick={() => navigate(T.revenueEstimate())}
            />
            <KpiCard
              testid="kpi-margin-estimate"
              title="Margin estimate"
              value={formatCurrency(kpis?.marginEstimate || 0)}
              subtitle={`${kpis?.details?.margin?.percent ?? 0}% margin`}
              icon={DollarSign}
              tone={kpis?.marginEstimate >= 0 ? 'positive' : 'critical'}
              onClick={() => navigate(T.marginEstimate())}
            />
          </div>
        </section>
      )}

      {/* ─── AI Insights (admin-tier only) ────────────────────────── */}
      {view.showAI && (
        <section>
          <SectionHeading testid="ai-insights-heading">
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              AI insights
            </span>
          </SectionHeading>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Card data-testid="ai-recommendations-widget" className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    Active recommendations
                  </CardTitle>
                  <Link to="/insights/recommendations" data-testid="ai-recommendations-view-all">
                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                      View all <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {aiInsights?.recommendations?.length > 0 ? (
                  <ul className="space-y-2.5">
                    {aiInsights.recommendations.slice(0, 3).map((rec, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <Badge
                          variant="outline"
                          className={
                            rec.priority === 'HIGH'
                              ? 'bg-red-50 text-red-700 border-red-200 text-[10px] uppercase'
                              : rec.priority === 'MEDIUM'
                                ? 'bg-amber-50 text-amber-700 border-amber-200 text-[10px] uppercase'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase'
                          }
                        >
                          {rec.priority}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-slate-800">{rec.message}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {rec.entityType} · {rec.entityName || rec.entityId}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500 py-3">No active recommendations.</p>
                )}
              </CardContent>
            </Card>

            <Card data-testid="ai-risk-summary-widget" className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    Risk overview
                  </CardTitle>
                  <Link to="/insights/risk" data-testid="ai-risk-view-all">
                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                      View all <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border border-red-200 bg-red-50/40 p-3 text-center">
                    <p className="text-xl font-semibold tabular-nums text-red-700">{aiInsights?.riskSummary?.highCount || 0}</p>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">High</p>
                  </div>
                  <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 text-center">
                    <p className="text-xl font-semibold tabular-nums text-amber-700">{aiInsights?.riskSummary?.mediumCount || 0}</p>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">Medium</p>
                  </div>
                  <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 text-center">
                    <p className="text-xl font-semibold tabular-nums text-emerald-700">{aiInsights?.riskSummary?.lowCount || 0}</p>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">Low</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Link to="/ai" data-testid="ai-copilot-link">
                    <Button variant="ghost" size="sm" className="gap-1.5 text-xs h-7">
                      <Bot className="h-3.5 w-3.5" /> Ask AI copilot
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* ─── Footer (data sources + period) ───────────────────────── */}
      <div
        data-testid="dashboard-footer"
        className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-2.5 text-[11px] text-slate-500 flex flex-wrap items-center justify-between gap-2"
      >
        <span>
          <span className="font-medium text-slate-600">Sources:</span> participants · staff · shifts · timesheets · incidents · medications · claims · payroll
        </span>
        <span className="tabular-nums">
          Generated {kpis?.generatedAt ? new Date(kpis.generatedAt).toLocaleString() : 'N/A'}
        </span>
      </div>
    </div>
  );
}
